import _ from 'lodash'
import React from 'react'
import PropTypes from 'prop-types'

import BrandContext from '../../contexts/BrandContext'

/**
 * getTitle - Returns a string for the title to display
 *
 * @param {boolean} [isCallCentre=false] flag to know if we are displaying this to the call centre or not
 * @param {object} brandConfig
 * @return {string} The title to show
 */
const getTitle = (isCallCentre = false, brandConfig) => {
  // Return when the call centre people see this
  if (isCallCentre) return 'Resort Server Issue'

  // Specific wording for resort
  if (_.get(brandConfig, 'secure.hasFeatures.callbackForm.resortWording', false)) {
    return 'Sorry, our resort hotels are currently unavailable'
  }

  // Return for normal customers
  return 'Sorry, our site is undergoing essential maintenance'
}

/**
 * getContent - Returns a string based on brand and adds the URL in it
 *
 * @param {string} brand Used to display a different piece depending on what is passed in
 * @param {string} callbackFormUrl URL to display as a link to the callback form
 * @param {string} helpCentreUrl URL to display as a link to the callback form
 * @param {boolean} [isCallCentre=false] flag to know if we are displaying this to the call centre or not
 * @param {string} parkName Used to display a different name depending on what is passed in
 * @param {object} brandConfig
 * @return {string} The content to show
*/
const getContent = (brand, callbackFormUrl, helpCentreUrl, isCallCentre = false, parkName, brandConfig) => {
  // Call centre specific content
  if (isCallCentre) {
    return (
      <div>
        <p>There is currently an issue with the resort hotel property management server.</p>
        <p>Customers may be populating the callback form with their details for callbacks. <strong>Please ensure your team leader is aware</strong>
        </p>
      </div>
    )
  }

  const callbackFormLink = (<a target='_blank' href={callbackFormUrl} dataCategory='Availability' dataAction='No Availability Callback' dataLabel={callbackFormUrl}>leave your details here</a>)

  // Specific wording for resort
  if (_.get(brandConfig, 'secure.hasFeatures.callbackForm.resortWording', false)) {
    return (
      <div>
        <p>
          This means we're unable to make or amend any resort bookings online or via the phone.
        </p>

        <p>
          If you would like one of our Guest Experience team to contact you when our resort hotels are available then please {callbackFormLink}.
        </p>
      </div>
    )
  }

  // Would be good to move this to prismic at some point to avoid hard coding
  const brandsWithCustomMessage = ['AT', 'AB', 'CH', 'LH', 'TH', 'PB', 'WR']
  const isbrandWithCustomMessage = brandsWithCustomMessage.some(brandsWithCustomMessage => brandsWithCustomMessage === brand)

  if (isbrandWithCustomMessage) {
    let breakType = 'short break'
    switch (brand) {
      case ('AT' || 'AB'):
        breakType = 'fantabulous short break'
        break
      case ('CH'):
        breakType = 'wild adventure'
    }

    let helpCentreLinkString
    if (helpCentreUrl) {
      helpCentreLinkString = (
        <React.Fragment>
          If you have general queries please visit our <a target='_blank' href={helpCentreUrl} dataCategory='Availability' dataAction='Help Centre Link' dataLabel={helpCentreUrl}>Help Centre</a>.
        </React.Fragment>
      )
    }

    return (
      <div>
        <p>
          Unfortunately, this means you won't be able to book your {breakType} with {parkName} just yet. We will also not be able to quote, check availability
          or make amendments to existing bookings through our Guest Experience Team.
        </p>

        <p>
          We're working hard to fix this so please try again soon. We aim to be back up and running shortly.
        </p>
        <p>
          {helpCentreUrl && helpCentreLinkString} If you would like one of our Guest Experience
          team to contact you, please {callbackFormLink} and we will get back to you as soon as we can. If you've left
          your details we will contact you shortly.
        </p>

      </div>
    )
  } else {
    const breakTitle = (brand === 'ET' || brand === 'HP') ? 'theatre break' : 'theme park break'
    return (
      <div>
        <p>
          We're currently making some improvements to our website,
          meaning that you won't be able to book your {breakTitle} just yet.
          Unfortunately this also means that our Customer Experience team won't be
          able to make any amendments to existing bookings for you during this time.
        </p>
        <p>
          We aim to be back up and running shortly.
          Please {callbackFormLink}, if you would like our
          Customer Experience Team to contact you when booking reopens.
        </p>
      </div>
    )
  }
}

/**
 * CallbackForm - Returns the rendered HTML
 *
 * @param {Object} props {isCallCentre}
 * @return {HTMLElement} HTML
 */
const CallbackForm = (props) => {
  return (
    <BrandContext.Consumer>
      {context => {
        const { brandConfig } = context
        const callbackFormUrl = _.get(brandConfig, 'secure.hasFeatures.callbackForm.url')
        const helpCentreUrl = _.get(brandConfig, 'secure.helpCentreUrl')
        const { brand, parkName } = brandConfig

        if (!brand || !callbackFormUrl || !parkName) return null
        const title = getTitle(props.isCallCentre, brandConfig)
        const content = getContent(brand, callbackFormUrl, helpCentreUrl, props.isCallCentre, parkName, brandConfig)

        return (
          <div className='panel panel-default'>
            <div className='panel-body'>
              {_.get(brandConfig, 'secure.hasFeatures.callbackForm.resortWording', false)
                ? <p>
                  <strong>{title}</strong>
                </p>
                : <h1>{title}</h1>
              }
              {content}
            </div>
          </div>
        )
      }}
    </BrandContext.Consumer>
  )
}

CallbackForm.propTypes = {
  isCallCentre: PropTypes.bool.isRequired
}

export default CallbackForm
